import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { donasiApi, DonasiListParams, SettlementSummaryParams } from '@/api/donasi';
import { Donasi } from '@/types';
import { useActionFeedback } from '@/hooks/useActionFeedback';

export const useDonasiList = (params: DonasiListParams = {}) => {
  return useQuery({
    queryKey: ['donasi', params],
    queryFn: () => donasiApi.getAll(params),
  });
};

export const useSettlementSummary = (params: SettlementSummaryParams = {}) => {
  return useQuery({
    queryKey: ['donasi', 'settlement-summary', params],
    queryFn: () => donasiApi.getSettlementSummary(params),
  });
};

export const useDonasi = (id: number) => {
  return useQuery({
    queryKey: ['donasi', id],
    queryFn: () => donasiApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateDonasi = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (data: Partial<Donasi>) => donasiApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['donasi'] });
      feedback.success('Transaksi donasi berhasil dibuat');
      return response.message;
    },
    onError: (error) => {
      feedback.error('Gagal membuat transaksi donasi', error);
    },
  });
};

export const useUpdateDonasi = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Donasi> }) =>
      donasiApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donasi'] });
      feedback.success('Transaksi donasi berhasil diperbarui');
    },
    onError: (error) => {
      feedback.error('Gagal memperbarui transaksi donasi', error);
    },
  });
};

export const useDeleteDonasi = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (id: number) => donasiApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donasi'] });
      feedback.success('Transaksi donasi berhasil dihapus');
    },
    onError: (error) => {
      feedback.error('Gagal menghapus transaksi donasi', error);
    },
  });
};
